import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useFocusTrap } from "../hooks/useFocusTrap";
import { API_BASE, authHeaders } from '../constants/api';
import Pagination from './Pagination';
import SearchWithSuggestions from './SearchWithSuggestions';
import { useDropdownClose } from '../hooks/useDropdownClose';
import { useAuth } from '../context/AuthContext';
import "./DashboardList.css";
import "./GroupDetails.css";
import { Zurück, Sortieren, Öffnen, Löschen, Abbrechen, Hinzufügen, Suche, Hinweis } from '../icons';
import { generateSuggestions } from '../utils/suggestions';
import { formatDateDE } from '../utils/formatDate';

const PAGE_SIZE = 10;

function DashboardGroupDetails() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const groupId = params.groupId;
  const { authToken: token } = useAuth();

  const [groupName, setGroupName] = useState(() => {
    return location?.state?.name || null;
  });

  const [dashboards, setDashboards] = useState([]);
  const [dashboardCount, setDashboardCount] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);
  const titleRef = useRef(null);

  const [sortOption, setSortOption] = useState("created");
  const [showSortOptions, setShowSortOptions] = useState(false);

  /* Löschen */
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const deleteConfirmRef = useRef(null);

  /* Dashboard hinzufügen */
  const [showAddDashboardsModal, setShowAddDashboardsModal] = useState(false);
  const [showAddDashboardsTooltip, setShowAddDashboardsTooltip] = useState(false);
  const [dashboardSearch, setDashboardSearch] = useState("");
  const [dashboardSuggestions, setDashboardSuggestions] = useState([]);
  const [showDashboardSuggestions, setShowDashboardSuggestions] = useState(false);
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [addingDashboards, setAddingDashboards] = useState(false);
  const [highlightedDashboardIndex, setHighlightedDashboardIndex] = useState(-1);
  const addDashboardsModalRef = useRef(null);
  const dashboardSuggestionsRef = useRef(null);

  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState('success');
  const [reloadKey, setReloadKey] = useState(0);

  const closeSortDropdown = useCallback(() => setShowSortOptions(false), []);
  useDropdownClose(showSortOptions, closeSortDropdown);
  useFocusTrap(showDeleteConfirm, deleteConfirmRef);
  useFocusTrap(showAddDashboardsModal, addDashboardsModalRef);

  function showStatus(message, type = 'success') {
    setStatusType(type);
    setStatusMessage(message);
    setTimeout(() => setStatusMessage(""), 4000);
  }

  useEffect(() => {
    if (groupName || !token || !groupId) return;
    async function loadGroupName() {
      try {
        const res = await fetch(
          `${API_BASE}/entityGroup/${groupId}`,
          { headers: authHeaders(token) }
        );
        if (res.ok) {
          const data = await res.json();
          setGroupName(data.name);
        }
      } catch { /* ignore */ }
    }
    loadGroupName();
  }, [token, groupId, groupName]);

  /* ===============================
     Dashboards der Gruppe laden
  =============================== */
  useEffect(() => {
    async function loadDashboards() {
      try {
        setLoading(true);
        setError("");

        const sortMap = {
          name: { property: "name", order: "ASC" },
          created: { property: "createdTime", order: "DESC" },
        };
        const sort = sortMap[sortOption];

        const params = new URLSearchParams({
          pageSize: PAGE_SIZE,
          page,
          sortProperty: sort.property,
          sortOrder: sort.order,
        });

        if (searchTerm) {
          params.append("textSearch", searchTerm);
        }

        const res = await fetch(
          `${API_BASE}/entityGroup/${groupId}/entities?${params.toString()}`,
          { headers: authHeaders(token) }
        );

        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Fehler beim Laden der Dashboards (HTTP ${res.status})`);
        }

        const data = await res.json();

        // Entities-Endpoint liefert kein createdTime → Dashboard-Info nachladen
        const entities = data.data || [];
        const normalizedDashboards = await Promise.all(
          entities.map(async (d) => {
            let createdTime = d.createdTime;
            if (!createdTime) {
              try {
                const infoRes = await fetch(
                  `${API_BASE}/dashboard/info/${d.id.id}`,
                  { headers: authHeaders(token) }
                );
                if (infoRes.ok) {
                  const info = await infoRes.json();
                  createdTime = info.createdTime;
                }
              } catch { /* ignore */ }
            }
            return {
              id: d.id.id,
              name: d.name || d.title,
              createdTime,
            };
          })
        );

        setDashboards(normalizedDashboards);
        setDashboardCount(data.totalElements ?? 0);
        setTotalPages(data.totalPages ?? 0);
      } catch (err) {
        setError(err.message || "Fehler beim Laden der Dashboards. Bitte prüfen Sie Ihre Netzwerkverbindung.");
        setDashboards([]);
        setDashboardCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    }

    if (token && groupId) loadDashboards();
  }, [token, groupId, page, sortOption, searchTerm, reloadKey]);

  // Focus title when loaded
  useEffect(() => {
    if (!loading && groupName && titleRef.current) {
      try { titleRef.current.focus(); } catch {}
    }
  }, [loading, groupName]);

  /* ===============================
     Dashboard aus Gruppe entfernen
  =============================== */
  async function removeDashboardFromGroup() {
    if (!dashboardToDelete) return;
    try {
      setRemoving(true);
      const res = await fetch(
        `${API_BASE}/entityGroup/${groupId}/deleteEntities`,
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify([dashboardToDelete.id])
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Dashboard konnte nicht aus der Gruppe entfernt werden (HTTP ${res.status})`);
      }
      setDashboards(prev => prev.filter(d => d.id !== dashboardToDelete.id));
      setDashboardCount(c => Math.max(0, c - 1));
      showStatus(`Dashboard \u201E${dashboardToDelete.name}\u201C wurde erfolgreich aus der Gruppe entfernt.`);
    } catch (e) {
      console.error(e);
      showStatus(`Dashboard konnte nicht aus der Gruppe entfernt werden: ${e.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setRemoving(false);
      setShowDeleteConfirm(false);
      setDashboardToDelete(null);
    }
  }

  /* ===============================
     Dashboard-Suche im Hinzufügen-Modal
  =============================== */
  useEffect(() => {
    if (!token) { setDashboardSuggestions([]); return; }
    const controller = new AbortController();
    async function fetchDashboards() {
      try {
        const params = new URLSearchParams({ pageSize: 10, page: 0 });
        if (dashboardSearch) params.set('textSearch', dashboardSearch);
        const res = await fetch(
          `${API_BASE}/dashboards/all?${params.toString()}`,
          { headers: authHeaders(token), signal: controller.signal }
        );
        if (!res.ok) return;
        const data = await res.json();
        const existingIds = new Set(dashboards.map(d => d.id));
        const selectedIds = new Set(selectedDashboards.map(d => d.id));
        setDashboardSuggestions(
          (data.data || []).filter(d => !existingIds.has(d.id.id) && !selectedIds.has(d.id.id))
        );
      } catch {}
    }
    fetchDashboards();
    return () => controller.abort();
  }, [dashboardSearch, dashboards, selectedDashboards, token]);

  function addDashboard(dashboard) {
    setSelectedDashboards(prev => [
      ...prev,
      { id: dashboard.id.id, name: dashboard.name || dashboard.title }
    ]);
    setDashboardSearch("");
    setDashboardSuggestions([]);
    setHighlightedDashboardIndex(-1);
    setShowDashboardSuggestions(false);
  }

  function removeSelectedDashboard(id) {
    setSelectedDashboards(prev => prev.filter(d => d.id !== id));
  }

  async function addDashboardsToGroup() {
    if (selectedDashboards.length === 0) return;
    try {
      setAddingDashboards(true);
      const res = await fetch(
        `${API_BASE}/entityGroup/${groupId}/addEntities`,
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify(selectedDashboards.map(d => d.id))
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Dashboards konnten nicht hinzugefügt werden (HTTP ${res.status})`);
      }
      showStatus(`${selectedDashboards.length} Dashboard(s) wurden erfolgreich hinzugefügt.`);
      setShowAddDashboardsModal(false);
      setSelectedDashboards([]);
      setDashboardSearch("");
      setPage(0);
      setReloadKey(k => k + 1);
    } catch (err) {
      showStatus(`Dashboards konnten nicht hinzugefügt werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setAddingDashboards(false);
    }
  }

  /* ===============================
     Such-Vorschläge
  =============================== */
  const suggestions = generateSuggestions(dashboards, searchTerm);

  /* ===============================
     Render
  =============================== */
  return (
    <div className="group-detail-page">
      {/* Header: Zurück-Button, Gruppenname, Sortierung, Suche – alles auf einer Linie */}
      <div className="device-detail-header">
        <div className="header-left">
          <button
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Zurück zur vorherigen Seite"
            title="Zurück zur vorherigen Seite"
          >
            <span aria-hidden="true"><Zurück /></span>
          </button>
          <h1 id="group-title" tabIndex={-1} ref={titleRef} className="device-title">
            Dashboard-Gruppe: {groupName ?? "Dashboard-Gruppe"} ({dashboardCount ?? "..."})
          </h1>
          <div className="sort-wrapper">
            <button
              className={`sort-button ${showSortOptions ? "active" : ""}`}
              onClick={() => setShowSortOptions(p => !p)}
              aria-expanded={showSortOptions}
              aria-haspopup="listbox"
              aria-label="Sortieroptionen anzeigen"
              title="Sortieroptionen anzeigen"
            >
              <span className="sort-icon"><Sortieren /></span> Sortieren
            </button>
            {showSortOptions && (
              <div className="sort-dropdown" role="listbox">
                {[{key: "name", label: "Name"}, {key: "created", label: "Hinzugefügt"}].map((opt) => (
                  <div
                    key={opt.key}
                    className={`sort-option ${sortOption === opt.key ? "active" : ""}`}
                    role="option"
                    aria-selected={sortOption === opt.key}
                    tabIndex={0}
                    onClick={() => { setSortOption(opt.key); setPage(0); setShowSortOptions(false); setTimeout(() => searchInputRef.current?.focus(), 0); }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        setSortOption(opt.key); setPage(0); setShowSortOptions(false); setTimeout(() => searchInputRef.current?.focus(), 0);
                      }
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="device-actions">
          <button
            className="export-button"
            aria-label="Dashboard hinzufügen"
            title="Dashboard hinzufügen"
            onClick={() => setShowAddDashboardsModal(true)}
          >
            <div className="export-icon"><Hinzufügen /></div>
            <div>Hinzufügen</div>
          </button>
          <SearchWithSuggestions
            ref={searchInputRef}
            id="dashboard-group-detail-search"
            label="Dashboard suchen"
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setPage(0); }}
            suggestions={suggestions}
            suggestionsId="dashboard-group-detail-suggestions"
          />
        </div>
      </div>

      {statusMessage && (
        <div className={`status-bar${statusType === 'error' ? ' status-bar--error' : ''}`} role="status" aria-live="polite">
          <p>{statusMessage}</p>
        </div>
      )}

      {/* Liste */}
      <div className="device-table-container" role="region" aria-label="Dashboardliste">
        {loading ? (
          <p>Dashboards werden geladen…</p>
        ) : error ? (
          <div role="alert" className="error-message">{error}</div>
        ) : (
          <>
            <div className="dashboard-list" role="list" aria-label="Dashboards">
              <div className="dashboard-row dashboard-header" role="row">
                <div>Dashboard-Name</div>
                <div>Hinzugefügt</div>
                <div>Öffnen</div>
                <div>Entfernen</div>
              </div>
              {(!dashboards || dashboards.length === 0) ? (
                <div className="no-items" style={{ padding: '12px 20px', color: '#474747' }}>Keine Dashboards in dieser Gruppe</div>
              ) : dashboards.map(d => (
                <div className="dashboard-row dashboard-item clickable-row" key={d.id} role="listitem"
                  onClick={(e) => {
                    if (e.target.closest('button, .sort-wrapper')) return;
                    navigate(`/dashboard/view/${d.id}`, { state: { name: d.name } });
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, .sort-wrapper')) {
                      e.preventDefault();
                      navigate(`/dashboard/view/${d.id}`, { state: { name: d.name } });
                    }
                  }}
                  tabIndex={0}
                  aria-label={`Dashboard ${d.name} öffnen`}
                >
                  <div>{d.name}</div>
                  <div>{formatDateDE(d.createdTime)}</div>
                  <div className="cell-right">
                    <button
                      className="icon-button open"
                      aria-label={`Dashboard ${d.name} öffnen`}
                      title={`Dashboard ${d.name} öffnen`}
                      tabIndex={0}
                      onClick={() => navigate(`/dashboard/view/${d.id}`, { state: { name: d.name } })}
                    >
                      <Öffnen />
                    </button>
                  </div>
                  <div className="cell-right">
                    <button
                      className="icon-button delete"
                      aria-label={`Dashboard ${d.name} aus der Gruppe entfernen`}
                      title={`Dashboard ${d.name} aus der Gruppe entfernen`}
                      tabIndex={0}
                      onClick={() => {
                        setDashboardToDelete({ id: d.id, name: d.name });
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <Löschen />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {dashboardCount > 0 && (
              <Pagination page={page} totalPages={totalPages} totalItems={dashboardCount} pageSize={PAGE_SIZE} onPageChange={setPage} label="Dashboards" />
            )}
          </>
        )}
      </div>

      {showAddDashboardsModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="add-dashboards-title">
          <div className="modal large" ref={addDashboardsModalRef}>
            <h2 id="add-dashboards-title">Dashboards zur Gruppe<br /><i>{groupName}</i> hinzufügen
              <button
                type="button"
                className={`tooltip-icon-button ${showAddDashboardsTooltip ? "active" : ""}`}
                aria-label="Informationen zum Hinzufügen von Dashboards"
                aria-expanded={showAddDashboardsTooltip}
                aria-controls="add-dashboards-tooltip"
                onClick={() => setShowAddDashboardsTooltip(prev => !prev)}
                onKeyDown={e => { if (e.key === "Escape") setShowAddDashboardsTooltip(false); }}
              >
                <Hinweis />
              </button>
            </h2>

            {showAddDashboardsTooltip && (
              <div
                id="add-dashboards-tooltip"
                role="tooltip"
                className="custom-tooltip"
              >
                Suchen Sie nach vorhandenen Dashboards über die Suchfunktion. Geben Sie dazu den Namen eines Dashboards ein.
                Wählen Sie mindestens ein Dashboard aus.
                Bereits hinzugefügte Dashboards werden nicht angezeigt.
              </div>
            )}

            <div className="groups-search-wrapper">
              <label htmlFor="add-dashboard-search" className="groups-search-label">
                Dashboard suchen
              </label>
              <span className="groups-search-icon"><Suche /></span>
              <input
                id="add-dashboard-search"
                className="groups-search-input"
                value={dashboardSearch}
                onChange={e => {
                  setDashboardSearch(e.target.value);
                  setHighlightedDashboardIndex(-1);
                }}
                onFocus={() => setShowDashboardSuggestions(true)}
                onBlur={() => setTimeout(() => setShowDashboardSuggestions(false), 150)}
                onKeyDown={e => {
                  if (e.key === "Escape") {
                    if (showDashboardSuggestions && dashboardSuggestions.length > 0) {
                      setShowDashboardSuggestions(false);
                      setHighlightedDashboardIndex(-1);
                    } else {
                      setShowAddDashboardsModal(false);
                    }
                    return;
                  }
                  if (!showDashboardSuggestions || dashboardSuggestions.length === 0) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlightedDashboardIndex(prev => Math.min(prev + 1, dashboardSuggestions.length - 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightedDashboardIndex(prev => Math.max(prev - 1, -1));
                  } else if (e.key === "Enter" && highlightedDashboardIndex >= 0) {
                    e.preventDefault();
                    addDashboard(dashboardSuggestions[highlightedDashboardIndex]);
                  }
                }}
                placeholder="Dashboard suchen"
                autoFocus
                aria-autocomplete="list"
                aria-controls="add-dashboard-suggestions"
                aria-activedescendant={
                  highlightedDashboardIndex >= 0 && showDashboardSuggestions && dashboardSuggestions.length > 0
                    ? `add-dashboard-suggestion-${highlightedDashboardIndex}`
                    : undefined
                }
              />
              {dashboardSearch && (
                <button
                  className="groups-search-clear"
                  onClick={() => {
                    setDashboardSearch("");
                    setHighlightedDashboardIndex(-1);
                  }}
                  aria-label="Eingabe zurücksetzen"
                  title="Eingabe zurücksetzen"
                >
                  ✖
                </button>
              )}
              {showDashboardSuggestions && dashboardSuggestions.length > 0 && (
                <div
                  id="add-dashboard-suggestions"
                  className="groups-search-suggestions"
                  role="listbox"
                  ref={dashboardSuggestionsRef}
                >
                  {dashboardSuggestions.map((d, idx) => (
                    <div
                      key={d.id.id}
                      id={`add-dashboard-suggestion-${idx}`}
                      className={`groups-search-suggestion ${
                        highlightedDashboardIndex === idx ? "highlighted" : ""
                      }`}
                      role="option"
                      aria-selected={highlightedDashboardIndex === idx}
                      onMouseDown={() => addDashboard(d)}
                      onMouseEnter={() => setHighlightedDashboardIndex(idx)}
                    >
                      {d.name || d.title}
                    </div>
                  ))}
                </div>
              )}
              {showDashboardSuggestions && dashboardSuggestions.length === 0 && dashboardSearch && (
                <div className="groups-search-suggestions" role="listbox">
                  <div className="groups-search-suggestion-no-results">Keine Ergebnisse</div>
                </div>
              )}
            </div>

            {selectedDashboards.length > 0 && (
              <div className="selected-badges">
                {selectedDashboards.map(d => (
                  <span key={d.id} className="selected-badge">
                    {d.name}
                    <button
                      type="button"
                      className="badge-remove"
                      onClick={() => removeSelectedDashboard(d.id)}
                      aria-label="Dashboard entfernen"
                      title="Dashboard entfernen"
                    >
                      ✖
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="modal-actions">
              <button
                className="modal-primary-positive"
                onClick={addDashboardsToGroup}
                disabled={addingDashboards || selectedDashboards.length === 0}
              >
                {addingDashboards ? "Wird hinzugefügt…" : "🔗 Dashboards hinzufügen"}
              </button>
              <button
                className="modal-secondary"
                onClick={() => {
                  setShowAddDashboardsModal(false);
                  setSelectedDashboards([]);
                  setDashboardSearch("");
                  setHighlightedDashboardIndex(-1);
                }}
                disabled={addingDashboards}
              >
                <Abbrechen /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && dashboardToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title">
          <div className="modal" ref={deleteConfirmRef}>
            <h2 id="delete-confirm-title">
              Sind Sie sicher, dass Sie das Dashboard <br/>
              <i>{dashboardToDelete.name}</i> aus der Gruppe entfernen möchten?
            </h2>

            <p>
              Das Dashboard bleibt erhalten und wird lediglich aus dieser Gruppe entfernt.
            </p>

            <div className="modal-actions">
              <button
                className="modal-primary"
                onClick={removeDashboardFromGroup}
                disabled={removing}
              >
                {removing ? (
                    "Wird entfernt…"
                  ) : (
                    <>
                      <Löschen />
                      Entfernen bestätigen
                    </>
                  )}
              </button>

              <button
                className="modal-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDashboardToDelete(null);
                }}
                disabled={removing}
              >
                <Abbrechen /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardGroupDetails;
